import {
  ABSENCE_REQUEST_FIELD_NAMES,
  ABSENCE_FIELD_NAMES,
  ABSENCE_ICONS,
  HOLIDAY,
} from '../../../constants/absence';
import { ABSENCE_REQUEST_STATUS_TYPES } from '../../../constants/statuses';
import { SINGLE_DAY_SAVE_FORMAT } from '../../../constants/date';
import { FIELD_TYPES } from '../../../constants/fieldTypes';
import { formatDate, subYearsFromDate } from '../../../utility/dateUtils';
import {
  prepareTableRequestDescription,
  prepareRequestPeriodLabel,
  prepareUpcomingTimeOffPeriodLabel,
} from '../../../utility/absence';
import {
  getItemById,
  isArrayEmpty,
  isObject,
  isObjectEmpty,
} from '../../../utility/helpers';
import { isEmpty, validateLength } from '../../../utility/validation';

const {
  IS_PREVIOUS_YEAR_MANUAL_BALANCE,
  CURRENT_YEAR_ADJUSTMENT_DAYS,
  TOTAL_PREVIOUS_YEAR,
  EXPIRATION_DATE,
} = ABSENCE_FIELD_NAMES;

const {
  ABSENCE,
  ABSENCE_FROM,
  ABSENCE_TO,
  DESCRIPTION,
  REASON,
  USED_DAYS,
  ABSENCE_SET,
} = ABSENCE_REQUEST_FIELD_NAMES;

export const BALANCE_INITIAL_DATA = {
  description: '',
  [CURRENT_YEAR_ADJUSTMENT_DAYS]: 0,
};

export const BALANCE_FIELDS = [
  {
    name: 'description',
    type: FIELD_TYPES.RICH_TEXT_EDITOR,
    translationKey: 'description',
    required: true,
    multiline: true,
    rows: 5,
    validators: [
      {
        validator: fieldValue => validateLength(fieldValue, 0, 10000),
        type: 'maxLength',
      },
      {
        type: 'required',
        validator: value => isEmpty(value),
      },
    ],
  },
  {
    name: CURRENT_YEAR_ADJUSTMENT_DAYS,
    type: FIELD_TYPES.NUMBER_STEPPER,
    translationKey: 'totalDaysAdjustment',
    required: true,
    validators: [{ type: 'required', validator: value => value !== 0 }],
  },
];

export const prepareUserRollOverBalanceInitialData = (absence, prevAbsence) => {
  if (prevAbsence) {
    return {
      ...prevAbsence,
      [TOTAL_PREVIOUS_YEAR]: prevAbsence.total,
    };
  }

  return {
    ...(absence
      ? {
          ...absence,
          [EXPIRATION_DATE]: formatDate(
            subYearsFromDate(new Date(absence[EXPIRATION_DATE])),
            SINGLE_DAY_SAVE_FORMAT
          ),
        }
      : {}),
  };
};

export const prepareUserRollOverBalanceField = classes => [
  {
    name: TOTAL_PREVIOUS_YEAR,
    type: FIELD_TYPES.TEXT,
    inputType: 'number',
    translationKey: 'numberOfDays',
    customRootClass: classes.numberOfDaysField,
    inputProps: {
      min: 1,
      max: 365,
    },
    validators: [
      { type: 'required', validator: isEmpty },
      {
        type: 'minMaxDays',
        validator: value => value >= 0,
      },
    ],
  },
  {
    name: ABSENCE_FIELD_NAMES.EXPIRATION_DATE,
    type: FIELD_TYPES.DATE_PICKER,
    translationKey: 'expirationDate',
    valueFormat: SINGLE_DAY_SAVE_FORMAT,
    isDisabled: true,
  },
];

export const prepareUserUpcomingTimeOffTableHeaders = (labels, classes) => [
  {
    id: 1,
    title: labels.period,
    rowKey: 'period',
    headerCellClass: classes.tableHeaderCell,
    rowCellClass: classes.tableRowCell,
    isSortingDisabled: true,
    minWidth: 220,
  },
  {
    id: 2,
    title: labels.description,
    rowKey: 'description',
    headerCellClass: classes.tableHeaderCell,
    rowCellClass: classes.tableRowCell,
    isSortingDisabled: true,
    hasConditionalTooltip: true,
    maxWidth: '1.5fr',
  },
  {
    id: 3,
    title: labels.used,
    rowKey: 'usedDays',
    headerCellClass: classes.tableHeaderCell,
    rowCellClass: classes.tableRowCell,
    isSortingDisabled: true,
    minWidth: 90,
  },
  {
    id: 4,
    title: labels.absence,
    rowKey: 'absenceComponent',
    headerCellClass: classes.tableHeaderCell,
    rowCellClass: classes.tableRowCell,
    isSortingDisabled: true,
    minWidth: 65,
  },
];

export const prepareUserUpcomingTimeOffTableData = (
  translations,
  upcomingTimeOff,
  userAbsences,
  renderAbsence
) => {
  if (isArrayEmpty(upcomingTimeOff) || isArrayEmpty(userAbsences)) return [];

  return upcomingTimeOff.reduce((acc, timeOff) => {
    const currentAbsence = timeOff?.[ABSENCE]
      ? getItemById(userAbsences, timeOff[ABSENCE].id, ABSENCE)
      : { ...HOLIDAY, name: translations[HOLIDAY.key] };
    const used = Math.abs(timeOff[USED_DAYS]);

    return [
      ...acc,
      {
        ...timeOff,
        absenceComponent: renderAbsence(currentAbsence),
        description:
          timeOff?.type === HOLIDAY.id
            ? timeOff[DESCRIPTION]
            : prepareTableRequestDescription(
                currentAbsence,
                timeOff[DESCRIPTION],
                timeOff[REASON]
              ),
        period: prepareUpcomingTimeOffPeriodLabel(
          timeOff[ABSENCE_FROM],
          timeOff[ABSENCE_TO]
        ),
        usedDays: used
          ? `${used} ${used === 1 ? translations.day : translations.days}`
          : '-',
      },
    ];
  }, []);
};

export const prepareUserAbsenceRequestsTableActionsHeader = (
  classes,
  labels
) => [
  {
    id: 'actions-column',
    title: labels.actions,
    rowKey: 'actions',
    align: 'right',
    headerCellClass: classes.tableHeaderCell,
    rowCellClass: classes.actionsCell,
    isHeaderAction: true,
    minWidth: 104,
    maxWidth: 104,
  },
];

export const prepareUserAbsenceRequestsTableHeaders = (classes, labels) => [
  {
    id: 1,
    title: labels.requestDate,
    rowKey: 'createdAt',
    headerCellClass: classes.tableHeaderCell,
    rowCellClass: classes.tableRowCell,
    cellValueClass: classes.cellValue,
    isSortingDisabled: true,
    minWidth: 125,
    maxWidth: 125,
  },
  {
    id: 2,
    title: labels.description,
    rowKey: 'description',
    headerCellClass: classes.tableHeaderCell,
    rowCellClass: classes.tableRowCell,
    isSortingDisabled: true,
    hasConditionalTooltip: true,
    maxWidth: '1.5fr',
  },
  {
    id: 3,
    title: labels.absence,
    rowKey: 'absenceComponent',
    headerCellClass: classes.tableHeaderCell,
    rowCellClass: classes.tableRowCell,
    isSortingDisabled: true,
    hasConditionalTooltip: true,
    minWidth: 65,
    maxWidth: 65,
  },
  {
    id: 4,
    title: labels.period,
    rowKey: 'period',
    headerCellClass: classes.tableHeaderCell,
    rowCellClass: classes.tableRowCell,
    isSortingDisabled: true,
    hasConditionalTooltip: true,
    minWidth: 260,
    maxWidth: 260,
  },
  {
    id: 5,
    title: labels.status,
    rowKey: 'statusComponent',
    headerCellClass: classes.tableHeaderCell,
    rowCellClass: classes.tableRowCell,
    isSortingDisabled: true,
    minWidth: 110,
    maxWidth: 110,
  },
];

export const prepareUserAbsenceRequestsTableData = (
  translations,
  auth,
  absenceRequests,
  requestStatuses,
  isDeactivatedUser,
  canManage,
  isRequestedByCurrentUser,
  isDoubleApprove,
  renderAbsence,
  renderStatus,
  renderActions
) => {
  if (isArrayEmpty(absenceRequests)) return [];

  return absenceRequests.reduce((acc, absenceRequest) => {
    const absence = absenceRequest[ABSENCE_REQUEST_FIELD_NAMES.ABSENCE];
    const hasUsedDays = absenceRequest?.used_days !== 0;
    const currentStatus = getItemById(requestStatuses, absenceRequest.status);
    const absenceIcon = getItemById(
      ABSENCE_ICONS,
      absenceRequest[ABSENCE_REQUEST_FIELD_NAMES.ABSENCE][
        ABSENCE_FIELD_NAMES.ICON
      ]
    )?.icon;
    const isRequestRejected =
      currentStatus.id === ABSENCE_REQUEST_STATUS_TYPES.REJECTED;
    const isRequestApproved =
      currentStatus.id === ABSENCE_REQUEST_STATUS_TYPES.APPROVED;
    const isHalfApproved =
      currentStatus?.id === ABSENCE_REQUEST_STATUS_TYPES.HALF_APPROVED;
    const isRequested =
      currentStatus?.id === ABSENCE_REQUEST_STATUS_TYPES.REQUESTED ||
      isHalfApproved;
    const isHalfApprovedBySuperior =
      isHalfApproved && absenceRequest.report_to && !absenceRequest.admin;
    const hasEdit =
      hasUsedDays &&
      ((canManage && !isRequestRejected) ||
        (isRequestedByCurrentUser && isRequested)) &&
      !isHalfApprovedBySuperior &&
      !isDeactivatedUser;
    const hasDelete =
      canManage || (isRequestedByCurrentUser && !isRequestApproved);

    const hasOpenReport =
      (canManage || isRequestedByCurrentUser) &&
      currentStatus.id === ABSENCE_REQUEST_STATUS_TYPES.APPROVED &&
      isObject(absenceRequest.pdf_report) &&
      !isObjectEmpty(absenceRequest.pdf_report);

    const hasManageRequestButtons =
      hasUsedDays &&
      (isDoubleApprove
        ? (canManage || absenceRequest?.user?.report_to === auth.id) &&
          !(
            absenceRequest?.user?.report_to === auth.id &&
            isHalfApprovedBySuperior
          )
        : canManage) &&
      isRequested &&
      !isDeactivatedUser;

    return [
      ...acc,
      {
        ...absenceRequest,
        createdAt: formatDate(absenceRequest.created_at),
        absenceComponent: renderAbsence({ ...absence, absenceIcon }),
        description: prepareTableRequestDescription(
          absence,
          absenceRequest[DESCRIPTION],
          absenceRequest[REASON]
        ),
        period: prepareRequestPeriodLabel(
          translations,
          absenceRequest[ABSENCE_FROM],
          absenceRequest[ABSENCE_TO],
          Math.abs(absenceRequest[USED_DAYS])
        ),
        statusComponent: renderStatus(currentStatus),
        ...(canManage || isRequestedByCurrentUser || hasManageRequestButtons
          ? {
              actions: renderActions(
                absenceRequest,
                hasManageRequestButtons,
                hasEdit,
                hasDelete,
                hasOpenReport
              ),
            }
          : {}),
      },
    ];
  }, []);
};

export const prepareUserBalanceTimelineTableHeaders = (classes, labels) => [
  {
    id: 1,
    title: labels.effectiveDate,
    rowKey: 'effectiveDate',
    headerCellClass: classes.tableHeaderCell,
    rowCellClass: classes.tableRowCell,
    cellValueClass: classes.cellValue,
    isSortingDisabled: true,
    minWidth: 125,
    maxWidth: 125,
  },
  {
    id: 2,
    title: labels.description,
    rowKey: 'description',
    headerCellClass: classes.tableHeaderCell,
    rowCellClass: classes.tableRowCell,
    cellValueClass: classes.descriptionCell,
    isSortingDisabled: true,
    maxWidth: '1.5fr',
  },
  {
    id: 3,
    title: labels.absence,
    rowKey: 'absenceComponent',
    headerCellClass: classes.tableHeaderCell,
    rowCellClass: classes.tableRowCell,
    isSortingDisabled: true,
    minWidth: 100,
    maxWidth: 100,
  },
  {
    id: 4,
    title: labels.accrual,
    rowKey: 'accrual',
    headerCellClass: classes.tableHeaderCell,
    rowCellClass: classes.tableRowCell,
    isSortingDisabled: true,
    minWidth: 100,
    maxWidth: 100,
  },
  {
    id: 5,
    title: labels.creator,
    rowKey: 'creator',
    headerCellClass: classes.tableHeaderCell,
    rowCellClass: classes.tableRowCell,
    isSortingDisabled: true,
    hasConditionalTooltip: true,
    minWidth: 100,
    maxWidth: 100,
  },
];

export const prepareUserBalanceTimelineTableData = (
  balanceTimeline,
  renderUser,
  renderAbsence,
  renderDescription
) => {
  if (isArrayEmpty(balanceTimeline)) return [];

  return balanceTimeline.reduce((acc, balance) => {
    const absence = balance[ABSENCE];
    const absenceIcon = getItemById(
      ABSENCE_ICONS,
      balance[ABSENCE][ABSENCE_FIELD_NAMES.ICON]
    )?.icon;
    const description = balance[DESCRIPTION] || '-';
    const preparedDescription =
      balance?.timeline_item_type === 'approved_request'
        ? prepareTableRequestDescription(
            absence,
            balance[DESCRIPTION],
            balance[REASON]
          )
        : description;

    return [
      ...acc,
      {
        ...balance,
        effectiveDate: formatDate(balance.date),
        description: renderDescription(preparedDescription),
        accrual: balance.days > 0 ? `+${balance.days}` : balance.days,
        absenceComponent: renderAbsence({ ...absence, absenceIcon }),
        creator:
          (balance.authors && !isArrayEmpty(balance.authors)) ||
          (isObject(balance.author) && !isObjectEmpty(balance.author))
            ? renderUser(balance.authors || [balance.author])
            : '-',
      },
    ];
  }, []);
};

export const isSameAbsenceSetCheck = (presentAbsences, previousAbsences) => {
  const [presentAbsence] = presentAbsences;
  const presentAbsenceSetId = presentAbsence?.[ABSENCE_SET];

  if (isArrayEmpty(presentAbsences) || isArrayEmpty(previousAbsences))
    return true;

  return previousAbsences.some(
    absence =>
      absence[ABSENCE_SET] === presentAbsenceSetId &&
      !absence[IS_PREVIOUS_YEAR_MANUAL_BALANCE]
  );
};
